import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import Resources from '@/components/resources/resources';
import { useRef, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DataCategoryModules, {
  DataCategoryModulesRef,
} from './components/health-data-categories/data-category-modules';
import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import { HealthDataEditorHeader } from './components/health-data-header/health-data-editor-header';
import { HealthDataEditorTitleDesktop } from './components/health-data-header/components/health-data-editor-title-desktop';
import { useGetHealthData, useRecalculateRiskScore } from './use-health-data';
import { RiskRecalculationBanner } from './components/risk-recalculation-banner';
import { RecalculationInterstitial } from './components/recalculation-interstitial';
export default function HealthDataPage() {
  const dataCategoryModulesRef = useRef<DataCategoryModulesRef>(null);
  const location = useLocation();
  const [showRecalculationInterstitial, setShowRecalculationInterstitial] = useState(false);

  // Get category name from navigation state
  const categoryName = location.state?.categoryName;

  const { isFetching, mappedCategories, healthData } = useGetHealthData();
  const { recalculate, hasResponse } = useRecalculateRiskScore();
  const canRecalculateRiskScore = ((healthData?.metadata?.pendingChanges ?? 0) > 0) && healthData?.metadata?.isReadyForSubmission;


  const handleRecalculate = async () => {
    if (!hasResponse) {
      console.warn('No questionnaire response found - user may need to complete the cancer risk assessment first');
      return;
    }

    // Show interstitial immediately
    setShowRecalculationInterstitial(true);

    recalculate({
      onSuccess: (data) => {
        setShowRecalculationInterstitial(false);
        // open risk impact modal here with returned data
      },
      onError: (error) => {
        console.error('Failed to recalculate risk score:', error);
        setShowRecalculationInterstitial(false);
      },
    });
  };

  // Scroll to category if one was passed via navigation state
  useEffect(() => {
    if (categoryName && dataCategoryModulesRef.current) {
      setTimeout(() => {
        dataCategoryModulesRef.current?.scrollToAndExpandCategory(categoryName);
      }, 300);
    }
  }, [categoryName]);

  // Map categories with a no-op onReviewClick (not used in edit mode)
  const categories = useMemo(
    () => mappedCategories.map(category => ({
      ...category,
      onReviewClick: () => {
        // No-op: in edit mode, expansion is handled by onToggleExpanded
      },
    })),
    [mappedCategories]
  );

  // Extract category names for navigation display
  const categoryNames = useMemo(
    () => categories.map(category => category.name),
    [categories]
  );

  return (
    <>
      <DashboardNavigationBar activeItem="health-data" />
      <HealthDataEditorHeader
        titleContent={<HealthDataEditorTitleDesktop />}
        categories={categoryNames}
        categoryModulesRef={dataCategoryModulesRef}
      />

      <DashboardLayout>
        {canRecalculateRiskScore && (
          <div className="lg:hidden mb-6">
            <RiskRecalculationBanner onRecalculate={handleRecalculate} />
          </div>
        )}

        <DashboardGrid>
          <DataCategoryModules
            ref={dataCategoryModulesRef}
            categories={categories}
            isEditMode={true}
            isFetching={isFetching}
          />
          <div className="flex flex-col gap-[24px]">
            {canRecalculateRiskScore && (
              <div className="hidden lg:block">
                <RiskRecalculationBanner onRecalculate={handleRecalculate} />
              </div>
            )}
            <Resources as="aside" width="full" />
          </div>
        </DashboardGrid>
      </DashboardLayout>

      {/* Recalculation Interstitial Overlay */}
      {showRecalculationInterstitial && (
        <div className="fixed inset-0 z-50">
          <RecalculationInterstitial />
        </div>
      )}
    </>
  );
}
